import React, { useEffect, useRef, useState } from 'react';
import { Loader2, UserRound, UserRoundPlus, X } from 'lucide-react';
import PropTypes from 'prop-types';

import { useToast } from '../../../../components/ui/toast/ToastContext';
import { camelizeKeys } from '../../../../utils/camelizeKeys';
import AccountForm from './AccountForm';
import AccountTabs from './AccountTabs';
import AccountView from './AccountView';
import { areaLabels, jobTitleLabels, profileArea, statusLabels } from './accountConstants';

const AccountModal = ({ mode, employeeId, accessProfiles, onClose, onSaved }) => {
  const [currentMode, setCurrentMode] = useState(mode);
  const [activeTab, setActiveTab] = useState('personal');
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(mode !== 'create');
  const [loadError, setLoadError] = useState('');
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);
  const { addToast } = useToast();

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    if (mode === 'create' || !employeeId) return undefined;

    const controller = new AbortController();

    const loadEmployee = async () => {
      setLoading(true);
      setLoadError('');

      try {
        const response = await fetch(`/app/master/employees/${employeeId}`, {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        const payload = camelizeKeys(await response.json());

        if (!response.ok) throw new Error(payload.errors?.join(' ') || 'Não foi possível carregar os dados da conta.');
        setEmployee(payload.employee);
      } catch (error) {
        if (error.name !== 'AbortError') {
          setLoadError(error.message);
          addToast({ type: 'error', message: error.message });
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadEmployee();
    return () => controller.abort();
  }, [addToast, employeeId, mode]);

  const isView = currentMode === 'view';
  const profile = employee?.accessProfile;
  const area = areaLabels[profileArea(profile)] || '—';
  const jobTitle = jobTitleLabels[profile?.jobTitle] || profile?.name || '—';

  const handleBackdropMouseDown = (event) => {
    if (event.target === event.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4"
      role="presentation"
      onMouseDown={handleBackdropMouseDown}
    >
      <section
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-modal-title"
        style={{ width: '920px', maxWidth: '100%', maxHeight: 'calc(100vh - 32px)', minHeight: '550px' }}
        className="flex overflow-y-auto rounded-xl bg-white p-6 shadow-lg"
      >
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex items-start justify-between gap-5">
            <div className="flex min-w-0 items-center gap-3">
              {isView && employee ? (
                <span className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-blueSoft text-tealMedium">
                  <UserRound size={23} strokeWidth={1.7} />
                </span>
              ) : currentMode === 'create' ? (
                <UserRoundPlus className="flex-none text-tealMedium" size={24} strokeWidth={1.7} />
              ) : (
                <UserRound className="flex-none text-tealMedium" size={24} strokeWidth={1.7} />
              )}

              <div className="min-w-0">
                <h2 id="account-modal-title" className="truncate text-xl font-semibold text-blueDarker">
                  {isView && employee
                    ? employee.fullName
                    : currentMode === 'create'
                    ? 'Adicionar conta'
                    : 'Editar conta'}
                </h2>
                {isView && employee && <p className="mt-1 text-sm text-blueSteel">{`${area} · ${jobTitle}`}</p>}
              </div>
            </div>

            <div className="flex flex-none items-center gap-4">
              {isView && employee && (
                <span
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${
                    employee.status === 'active'
                      ? 'bg-permissionEditBg text-greenBright'
                      : 'bg-redExtraLight2 text-redBase'
                  }`}
                >
                  <span
                    className={`h-2 w-2 rounded-full ${
                      employee.status === 'active' ? 'bg-permissionEditDot' : 'bg-redHighlight'
                    }`}
                  />
                  {statusLabels[employee.status] || employee.status}
                </span>
              )}
              <button
                ref={closeButtonRef}
                type="button"
                aria-label="Fechar modal"
                onClick={onClose}
                className="text-blueSteel transition-colors hover:text-blueDarker focus:outline-none"
              >
                <X size={24} strokeWidth={1.7} />
              </button>
            </div>
          </header>

          <AccountTabs activeTab={activeTab} onChange={setActiveTab} />

          {loading ? (
            <div className="flex flex-1 items-center justify-center text-sm text-blueSteel">
              <span className="inline-flex items-center gap-2">
                <Loader2 className="animate-spin text-tealMedium" size={20} />
                Carregando dados da conta...
              </span>
            </div>
          ) : loadError ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
              <p className="text-sm text-redBase">{loadError}</p>
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-grayExtraLight2 px-5 py-2 text-sm font-semibold text-blueDarker"
              >
                FECHAR
              </button>
            </div>
          ) : isView ? (
            <AccountView
              employee={employee}
              activeTab={activeTab}
              onClose={onClose}
              onEdit={() => setCurrentMode('edit')}
            />
          ) : (
            <AccountForm
              mode={currentMode}
              employee={employee}
              accessProfiles={accessProfiles}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              onCancel={onClose}
              onSaved={onSaved}
            />
          )}
        </div>
      </section>
    </div>
  );
};

AccountModal.propTypes = {
  mode: PropTypes.oneOf(['create', 'view', 'edit']).isRequired,
  employeeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  accessProfiles: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

AccountModal.defaultProps = {
  employeeId: null,
};

export default AccountModal;
